import { Suspense, useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { probe } from './api/client';
import { deliverCapture, registerCameraOpener } from './capture';
import { readDetailState } from './nav';
import { useRouteScroll } from './routeScroll';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireAuth, loginHref } from './components/AccessDenied';
import ErrorBoundary from './components/ErrorBoundary';
import { Icon, type IconName } from './components/Icon';
import Illustration from './components/Illustration';
import ModeBanner from './components/ModeBanner';
import RouteSkeleton from './components/RouteSkeleton';
import { RoleBadge } from './components/RoleBadge';
import ThemeToggle from './components/ThemeToggle';
import Toaster, { toast } from './components/Toast';
import TraySlot from './components/TraySlot';
import { useTray } from './tray';
import { authGen } from './api/auth';
import { lazyRetry } from './lazyRetry';
import Home from './pages/Home';
import Results from './pages/Results';

/* 관리 · 계정 · 전체 원단 화면은 필요할 때만 내려받는다 — 검색 화면 번들을 300KB 안으로 (루브릭 H3) */
/* 담은 원단 트레이(버튼 · 패널)도 지연 청크 — 담은 원단이 있을 때만 그린다. 한가할 때 · 처음 담을 때 미리 받는다(11회차, 주 번들 여유) */
/* 12회차 Codex 4차 N15: 트레이 청크를 못 받아도 앱 전체가 멈추지 않게 — 트레이 전용 오류 경계 + 다시 받기(TraySlot) */
const loadTray = () => import('./components/Tray');
const loadBrowse = () => import('./pages/Browse');
/* 14회차 m63 · N30: 화면 청크는 lazyRetry — 미리 받기가 한 번 실패해도 그릴 때 새로 받고, 오류 화면의 「다시 시도」 가 실제로 다시 받는다 */
const Browse = lazyRetry(loadBrowse);
/* 13회차: 원단 상세도 지연 청크(Codex 5차 제안 — 주 번들 여유 1.7KB 뿐이었다). 첫 화면이 한가할 때 미리 받아 카드를 누르면 바로 열린다.
   청크를 받는 동안은 상세 자신의 골격과 같은 틀(RouteSkeleton → DetailSkeleton)이라 자리가 움직이지 않는다 */
const loadDetail = () => import('./pages/Detail');
const Detail = lazyRetry(loadDetail);
/* 로그인 화면(소개 칸 포함)도 지연 청크 — 첫 화면(검색)에는 필요 없다 */
const Login = lazyRetry(() => import('./pages/Login'));
/* 사진 검색(자르기 · 최근 기록 포함)도 지연 청크 — 홈 「카메라로 찍어서 찾기」 사진은 capture.ts 가 받아 두었다가 넘긴다 */
const ImageSearch = lazyRetry(() => import('./pages/ImageSearch'));
const Account = lazyRetry(() => import('./pages/Account'));
const Signup = lazyRetry(() => import('./pages/Signup'));
/* 관리 틀 — 안에 Suspense 가 있어 관리 화면끼리 옮길 때는 메뉴가 남고 본문 자리만 골격(9회차 m44) */
const AdminShell = lazyRetry(() => import('./components/AdminShell'));
const AdminAudit = lazyRetry(() => import('./pages/AdminAudit'));
const AdminDashboard = lazyRetry(() => import('./pages/AdminDashboard'));
const AdminReview = lazyRetry(() => import('./pages/AdminReview'));
const AdminUsers = lazyRetry(() => import('./pages/AdminUsers'));
/* 분석 실패 목록(대시보드 「오늘 할 일 · 분석 실패」 가 가는 곳 — 서버 GET /admin/failed, 2026-10-18) */
const AdminFailed = lazyRetry(() => import('./pages/AdminFailed'));
/* 관리자 원단 편집 · 휴지통(루브릭 R4, 2026-10-18 서버) — 관리자만, 지연 청크 */
const AdminFabricEdit = lazyRetry(() => import('./pages/AdminFabricEdit'));
const AdminTrash = lazyRetry(() => import('./pages/AdminTrash'));
/* 비교 · 컬렉션(계획서 U1)도 지연 로딩 — 주 번들 300KB 안 */
const Compare = lazyRetry(() => import('./pages/Compare'));
const Collections = lazyRetry(() => import('./pages/Collections'));
const CollectionDetail = lazyRetry(() => import('./pages/CollectionDetail'));
/* 저장한 검색(계획서 U17) · 가입 뒤 승인 대기 화면(U13)도 지연 청크 */
const SavedSearches = lazyRetry(() => import('./pages/SavedSearches'));
const SignupPending = lazyRetry(() => import('./pages/SignupPending'));

/** 상단바 오른쪽 — 로그인 버튼 또는 사용자 메뉴. */
function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!user) {
    const here = loc.pathname + loc.search;
    const onAuthPage = loc.pathname === '/login' || loc.pathname === '/signup';
    return (
      <Link className="btn login-btn" to={loginHref(onAuthPage ? undefined : here)}>
        <Icon name="login" size={18} /><span className="login-btn-text">로그인</span>
      </Link>
    );
  }

  async function doLogout() {
    setOpen(false);
    // 홈으로 먼저 — 로그인 지우기(logout 의 첫 동기 구간)와 같은 그리기에 묶여, 관리 · 계정 화면의 로그인 가드가
    // /login 골격을 한 번 스치게 하지 않는다(13회차 관찰 ⑦)
    nav('/', { replace: true });
    const g = await logout();
    // 서버에 알리는 동안 다시 로그인했으면(세대가 바뀜) 새 세션에 「로그아웃되었습니다」 를 띄우지 않는다(N20)
    if (authGen() !== g) return;
    toast('로그아웃되었습니다', 'ok', { sid: g });
  }

  return (
    <>
    <div className="user-menu" ref={box}>
      <button type="button" className="user-menu-btn" aria-haspopup="menu" aria-expanded={open}
              aria-label={`${user.display_name} 계정 메뉴`} onClick={() => setOpen((o) => !o)}>
        {/* 480px 이하에서는 이름 대신 머리글자만 — 관리자 메뉴까지 켜지면 바가 넘쳤다 */}
        <span className="user-menu-initial" aria-hidden="true">{user.display_name.trim().charAt(0) || '?'}</span>
        <span className="user-menu-name">{user.display_name}</span>
        <RoleBadge role={user.role} />
      </button>
      {open && (
        <div className="user-menu-list" role="menu">
          {/* 리뷰 5회차 m22: 480px 이하에서는 버튼의 역할 배지를 숨기므로 메뉴 머리에 역할을 둔다.
              R1-5: 사용자 식별은 이메일 — 이메일이 없는 기존 계정만 아이디를 보인다 */}
          <div className="user-menu-id">
            <span className="user-menu-mail">{user.email || user.user_id}</span>
            <RoleBadge role={user.role} />
          </div>
          <Link role="menuitem" to="/account"><Icon name="user" size={18} /> 내 정보</Link>
          <Link role="menuitem" to="/collections"><Icon name="folder-stack" size={18} /> 내 컬렉션</Link>
          <Link role="menuitem" to="/saved-searches"><Icon name="bookmark-search" size={18} /> 저장한 검색</Link>
          {/* 720px 이하에서는 상단 내비가 아래 탭 막대로 바뀌어 「관리자」 자리가 없다 — 메뉴에서 */}
          {user.role === 'admin' && <Link role="menuitem" to="/admin" className="only-mobile"><Icon name="shield" size={18} /> 관리자 화면</Link>}
          {user.role === 'admin' && <Link role="menuitem" to="/admin/users"><Icon name="users" size={18} /> 사용자 관리</Link>}
          {user.role === 'admin' && <Link role="menuitem" to="/admin/audit"><Icon name="history" size={18} /> 감사 기록</Link>}
          <button role="menuitem" type="button" onClick={() => void doLogout()}><Icon name="logout" size={18} /> 로그아웃</button>
        </div>
      )}
    </div>
    {/* 발주 측 지적(2026-10-05): "로그인했으면 로그아웃 버튼도 어딘가 있어야" — 메뉴 안에만 있으면
        찾지 못한다. 상단바에 늘 보이게 둔다. 좁은 폭에서는 아이콘만, 이름은 aria-label. */}
    <button type="button" className="btn sm logout-btn" onClick={() => void doLogout()}
            aria-label="로그아웃" title="로그아웃">
      <Icon name="logout" size={16} /><span className="logout-btn-text">로그아웃</span>
    </button>
    </>
  );
}

type Section = 'search' | 'image' | 'browse' | 'admin' | 'account' | null;

/**
 * 지금 화면이 주 내비의 어느 칸인지 (리뷰 6회차 m35: 검색 결과 · 상세에서 아무 칸도 켜지지 않았다).
 * 상세는 들어온 목록을 따른다 — 전체 원단에서 왔으면 「전체 원단」, 사진 검색에서 왔으면 「사진으로 검색」.
 */
function sectionOf(pathname: string, state: unknown): Section {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/account') || pathname.startsWith('/collections') || pathname.startsWith('/saved-searches')) return 'account';
  if (pathname.startsWith('/browse')) return 'browse';
  if (pathname.startsWith('/image-search')) return 'image';
  if (pathname === '/' || pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/fabric/')) {
    const from = readDetailState(state).from ?? '';
    if (from.startsWith('/browse')) return 'browse';
    if (from.startsWith('/image-search')) return 'image';
    return 'search';
  }
  return null;
}

/** 주 내비 한 칸 — 아이콘 18 + 글자. 좁은 폭에서는 글자를 숨기고 aria-label 이 이름을 맡는다 (R1-3). */
function NavItem({ to, icon, label, on, exact }: { to: string; icon: IconName; label: string; on: boolean; exact: boolean }) {
  return (
    <Link to={to} aria-label={label} title={label} className={on ? 'on' : undefined}
          aria-current={on ? (exact ? 'page' : 'true') : undefined}>
      <Icon name={icon} size={18} />
      <span className="nav-text">{label}</span>
    </Link>
  );
}

/**
 * 휴대폰 아래 탭 막대 (720px 이하, 컨셉 §7.6) — 엄지가 닿는 곳에 4칸. 아이콘 24 + 글자 11, 높이 56 + 안전 영역.
 */
function TabBar({ section }: { section: Section }) {
  const { user } = useAuth();
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  const onAuth = loc.pathname === '/login' || loc.pathname === '/signup';
  const items: Array<{ to: string; icon: IconName; label: string; on: boolean; exact: boolean }> = [
    { to: '/', icon: 'search', label: '검색', on: section === 'search', exact: loc.pathname === '/' },
    { to: '/image-search', icon: 'camera', label: '사진 검색', on: section === 'image', exact: loc.pathname === '/image-search' },
    { to: '/browse', icon: 'grid', label: '전체 원단', on: section === 'browse', exact: loc.pathname === '/browse' },
    user
      ? { to: '/account', icon: 'user', label: '내 정보', on: section === 'account', exact: loc.pathname === '/account' }
      : { to: loginHref(onAuth ? undefined : here), icon: 'login', label: '로그인', on: onAuth, exact: onAuth },
  ];
  return (
    <nav className="tabbar" aria-label="아래 탭 메뉴">
      {items.map((it) => (
        <Link key={it.label} to={it.to} className={it.on ? 'on' : undefined}
              aria-current={it.on ? (it.exact ? 'page' : 'true') : undefined}>
          <Icon name={it.icon} size={24} />
          <span>{it.label}</span>
        </Link>
      ))}
    </nav>
  );
}

/**
 * 홈(휴대폰) 「카메라로 찍어서 찾기」의 카메라 입력 — 앱 틀에 늘 붙어 있어 버튼을 누른 그 순간 열 수 있다.
 * 찍은 사진은 capture.ts 에 맡기고 사진 검색 화면이 꺼내 간다.
 */
function CameraInput() {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    registerCameraOpener(() => ref.current?.click());
    return () => registerCameraOpener(null);
  }, []);
  return (
    <input ref={ref} type="file" accept="image/*" capture="environment" hidden tabIndex={-1}
           aria-label="카메라로 원단 찍기"
           onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) deliverCapture(f); }} />
  );
}

function Shell() {
  const loc = useLocation();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  // 12회차 M28: 화면을 바꾸면 맨 위, 뒤로 · 앞으로는 떠날 때 위치, 같은 화면 질의 변경은 그 화면 규칙(routeScroll.ts)
  useRouteScroll();
  // 13회차 M29: 세션과 무관한 안내(탈퇴 완료 등)는 화면 이동의 상태({ notice })로 받아 옮긴 화면에서 한 번 알린다 —
  // 로그인을 지운 뒤 그린 화면이라 지금 세대의 토스트다(앞 세션 토스트처럼 로그인 지우기에 내려가지 않는다).
  // 새로 고침 · 뒤로 가기로 다시 뜨지 않게 기록에서 지운다. 같은 기록을 두 번 알리지 않는다(개발 모드 effect 두 번 실행)
  const noticed = useRef<string | null>(null);
  useEffect(() => {
    const n = (loc.state as { notice?: unknown } | null)?.notice;
    if (typeof n !== 'string' || noticed.current === loc.key) return;
    noticed.current = loc.key;
    toast(n, 'ok');
    nav(loc.pathname + loc.search + loc.hash, { replace: true, state: null });
  }, [loc.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const section = sectionOf(loc.pathname, loc.state);
  // 담은 원단 트레이 — 로그인 · 가입 · 관리 화면에서는 띄우지 않는다(그 화면의 일과 무관)
  const tray = useTray();
  const trayOn = tray.length > 0 && !/^\/(login|signup|admin)/.test(loc.pathname);
  // 휴대폰 사진 검색은 실행 막대가 화면 아래 고정 — 바닥글 · 트레이 · 토스트가 그 위에 오게 (리뷰 7회차 m38)
  const runbar = loc.pathname.startsWith('/image-search');

  // 서버 확인은 기동 시 한 번 — 결과는 모의 데이터일 때만 ModeBanner 가 알린다.
  // R1-2: 상단의 「서버 연결」 배지는 누를 수도 없고 사용자 행동과 무관해 없앴다.
  useEffect(() => { void probe(); }, []);
  // 주 메뉴 「전체 원단」 청크는 첫 화면이 다 그려진 뒤 한가할 때 미리 받아 둔다 — 누르면 골격 없이 바로 열리게(못 받아도 골격이 맡는다)
  // 15회차 M33: 「한가할 때」 가 JS 실행 직후라 느린 망에서 첫 화면 목록 · 사진 요청과 겹쳤다(54KB) — 화면 안 사진이 다 오고
  // 불러오는 중인 구역이 없을 때까지(최대 12초) 기다린 뒤 받는다. 빠른 망에서는 1초 안에 끝난다
  useEffect(() => { if (trayOn) void loadTray().catch(() => undefined); }, [trayOn]);
  useEffect(() => {
    let timer = 0;
    let stop = false;
    const t0 = performance.now();
    const go = () => {
      void loadBrowse().catch(() => undefined); void loadTray().catch(() => undefined); void loadDetail().catch(() => undefined);
    };
    const inView = (el: Element) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight; };
    // 화면 안에서 아직 불러오는 구역(골격 aria-busy) · 아직 오지 않은 사진 — 화면 밖의 미룬 줄은 세지 않는다
    const busy = () => Array.from(document.querySelectorAll('main [aria-busy="true"]')).some(inView)
      || Array.from(document.images).some((i) => !i.complete && inView(i));
    const tick = () => {
      if (stop) return;
      if (busy() && performance.now() - t0 < 12_000) { timer = window.setTimeout(tick, 500); return; }
      go();
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(tick, { timeout: 4000 }); else timer = window.setTimeout(tick, 2500);
    return () => { stop = true; window.clearTimeout(timer); };
  }, []);

  return (
    <div className={`app${trayOn ? ' has-tray' : ''}${runbar ? ' has-runbar' : ''}`}>
      {/* R1-8: 상단바 배경만 화면 끝까지 — 내용은 본문 기둥(.page) 양 끝에 맞춘다 */}
      <header className="topbar">
        <div className="topbar-in page">
        <Link to="/" className="logo" aria-label="AI TEX 홈">AI <span>TEX</span></Link>
        <nav aria-label="주 메뉴" className="topnav">
          <NavItem to="/" icon="search" label="원단 검색" on={section === 'search'} exact={loc.pathname === '/'} />
          <NavItem to="/image-search" icon="image-search" label="사진으로 검색" on={section === 'image'} exact={loc.pathname === '/image-search'} />
          <NavItem to="/browse" icon="grid" label="전체 원단" on={section === 'browse'} exact={loc.pathname === '/browse'} />
          {isAdmin && <NavItem to="/admin" icon="shield" label="관리자" on={section === 'admin'} exact={loc.pathname === '/admin'} />}
        </nav>
        <span className="sp" />
        <ThemeToggle />
        <UserMenu />
        </div>
      </header>

      <ModeBanner />
      {/* 알림 영역은 #root 밖(body 포털)에 그린다 — 창이 열려 본문이 inert 여도 낭독된다(10회차 m47) */}
      <Toaster tray={trayOn} runbar={runbar} />
      <CameraInput />

      <main key={loc.pathname}>
        {/* 리뷰 8회차 관찰 ⑦: 한 화면의 그리기 오류가 앱 전체를 흰 화면으로 만들지 않게 — 상단바 · 탭 막대는 남는다 */}
        {/* Codex 검토: 경로만 보면 /search?q=A 오류 뒤 q 만 바꿔도 풀리지 않았다 — 경로 + 질의 */}
        <ErrorBoundary resetKey={loc.pathname + loc.search}>
        {/* 9회차 m44: 처음 들어가는 화면(청크 미적재)도 글자 대신 그 화면의 골격 */}
        <Suspense fallback={<RouteSkeleton pathname={loc.pathname} />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Results />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/image-search" element={<ImageSearch />} />
          <Route path="/fabric/:id" element={<Detail />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/collections" element={<RequireAuth><Collections /></RequireAuth>} />
          <Route path="/collections/:id" element={<RequireAuth><CollectionDetail /></RequireAuth>} />
          <Route path="/saved-searches" element={<RequireAuth><SavedSearches /></RequireAuth>} />
          <Route path="/signup/pending" element={<SignupPending />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
          {/* R1-8: 관리 화면은 기둥 안 왼쪽 레일에 관리 메뉴(AdminShell) — 권한 확인은 레일까지 감싼다 */}
          <Route path="/admin" element={<RequireAuth role="admin"><AdminShell /></RequireAuth>}>
            <Route index element={<AdminDashboard />} />
            <Route path="review" element={<AdminReview />} />
            <Route path="failed" element={<AdminFailed />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="trash" element={<AdminTrash />} />
            <Route path="fabric/:id/edit" element={<AdminFabricEdit />} />
          </Route>
          <Route path="*" element={
            <div className="state empty">
              <Illustration name="not-found" />
              <h1 className="state-title">페이지를 찾을 수 없습니다.</h1>
              <p>주소가 바뀌었거나 삭제된 화면입니다. 원단 검색에서 다시 찾아보세요.</p>
              <Link className="btn sm" to="/"><Icon name="home" size={16} /> 홈으로</Link>
            </div>
          } />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>

      <footer className="footer">
        <div className="page">AI TEX 원단 데이터 플랫폼 · AITEX-SPEC-001 Rev.1.1 · 플러스유㈜</div>
      </footer>
      {trayOn && <TraySlot load={loadTray} count={tray.length} />}
      <TabBar section={section} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
